import React, { useState } from 'react'
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { getFunctions, httpsCallable } from 'firebase/functions'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RjLTXPf7U0kOHcwMl8aHLyumyrTGjRKw6OKCT59JkYyLtrLECMiEq0R9icSCQJ5lQP390Yyk8VUfNiSRSivnXV5004V5IiAjj')

interface UpdatePaymentMethodModalProps {
  show: boolean
  onHide: () => void
  onPaymentMethodUpdated: (paymentMethod: any) => void
  currentPaymentMethod?: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  } | null
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
}

const PaymentMethodForm: React.FC<{
  onSubmit: (paymentMethodId: string) => Promise<void>
  isProcessing: boolean
  error: string | null
}> = ({ onSubmit, isProcessing, error }) => {
  const stripe = useStripe()
  const elements = useElements()
  const functions = getFunctions()
  const createStripeUserAccount = httpsCallable(functions, 'createStripeUserAccount')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      return
    }
    const result = await createStripeUserAccount();
    const data = result.data as any
    if (!data.success) {
        throw new Error(data.error || 'Error creating payment method')
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (error) {
      console.error('Error creating payment method:', error)
      return
    }

    if (paymentMethod) {
      await onSubmit(paymentMethod.id)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Form.Group className="mb-3">
        <Form.Label>New Payment Method</Form.Label>
        <div className="form-control" style={{ padding: '12px' }}>
          <CardElement options={cardElementOptions} />
        </div>
      </Form.Group>

      <div className="d-flex justify-content-end gap-2">
        <Button 
          type="submit" 
          variant="primary"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
              Updating...
            </>
          ) : (
            'Update Payment Method'
          )}
        </Button>
      </div>
    </Form>
  )
}

const UpdatePaymentMethodModal: React.FC<UpdatePaymentMethodModalProps> = ({
  show,
  onHide,
  onPaymentMethodUpdated,
  currentPaymentMethod
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const functions = getFunctions()
  const updatePaymentMethod = httpsCallable(functions, 'updatePaymentMethod')

  const handlePaymentMethodUpdate = async (paymentMethodId: string) => {
    setIsProcessing(true)
    setError(null)

    try {
      const result = await updatePaymentMethod({ paymentMethodId })
      const data = result.data as any

      if (data.success) {
        setSuccess(true)
        onPaymentMethodUpdated(data.paymentMethod)
        
        // Close modal after a short delay to show success
        setTimeout(() => {
          setSuccess(false)
          onHide()
        }, 1500)
      } else {
        setError(data.message || 'Failed to update payment method')
      }
    } catch (err: any) {
      console.error('Error updating payment method:', err)
      setError('Failed to update payment method. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    if (!isProcessing) {
      setError(null)
      setSuccess(false)
      onHide()
    }
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton={!isProcessing}>
        <Modal.Title>Update Payment Method</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {success ? (
          <Alert variant="success" className="text-center">
            <h5>✅ Payment Method Updated Successfully!</h5>
            <p>Your new payment method has been saved and will be used for future billing.</p>
          </Alert>
        ) : (
          <>
            {currentPaymentMethod && (
              <div className="mb-4 p-3 bg-light rounded">
                <h6>Current Payment Method</h6>
                <p className="mb-0">
                  {currentPaymentMethod.brand.toUpperCase()} ending in {currentPaymentMethod.last4}
                  <br />
                  <small className="text-muted">
                    Expires {currentPaymentMethod.exp_month}/{currentPaymentMethod.exp_year}
                  </small>
                </p>
              </div>
            )}

            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                onSubmit={handlePaymentMethodUpdate}
                isProcessing={isProcessing}
                error={error}
              />
            </Elements>
          </>
        )}
      </Modal.Body>

      {!success && (
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default UpdatePaymentMethodModal
